'use client';

/**
 * RetouchFlow Provider Component
 *
 * Main entry point for the returning visitor discount flow.
 * Wrap your Ring/Buy pages with this provider to enable the flow.
 *
 * @example
 * ```tsx
 * // In pages/ring/buy/us.tsx
 * import { RetouchFlowProvider } from 'components/shared/RetouchFlow';
 *
 * const RingBuyUS = () => {
 *   return (
 *     <RetouchFlowProvider>
 *       <RingSelectionContextProvider>
 *         // ... page content
 *       </RingSelectionContextProvider>
 *     </RetouchFlowProvider>
 *   );
 * };
 * ```
 */

import React, { useEffect, useState, useRef } from 'react';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { analytics } from 'lib/analytics/analytics';
import { allowsDataLayer } from 'lib/consent/gate';
import { useRetouchFlow } from './useRetouchFlow';
import { RetouchFlowContext } from './RetouchFlowContext';
import { RetouchModalContent } from './RetouchModalContent';
import {
  MODAL_DELAY_MS,
  BUY_PAGE_PATTERN,
  ANALYTICS_EVENTS,
  GTM_EVENTS,
  VISITS_THRESHOLD,
} from './constants';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { useModal } from 'components/shared/Modal';
import { getCountrySpecificConfig } from 'lib/region/getCountrySpecificConfig';

interface RetouchFlowProviderProps {
  children: React.ReactNode;
  /** Override the default delay before showing modal (ms) */
  delayMs?: number;
  /** Disable the flow entirely (useful for testing) */
  disabled?: boolean;
  /** Skip campaign check - show modal even if a Ring campaign is active */
  ignoreCampaigns?: boolean;
  /** Country/locale code for country-specific config (e.g. 'us', 'gb') */
  locale?: string;
}

/**
 * RetouchFlowProvider
 *
 * Provides the RetouchFlow context to children and manages the modal display.
 * The modal will show after a delay when the user meets all criteria:
 * - 4+ visits to /ring/* pages
 * - Currently on a /ring/buy/* page
 * - Modal not already shown
 * - No affiliateCode in URL
 * - No active Ring campaign (unless ignoreCampaigns is true)
 */
export const RetouchFlowProvider: React.FC<RetouchFlowProviderProps> = ({
  children,
  delayMs = MODAL_DELAY_MS,
  disabled = false,
  ignoreCampaigns = false,
  locale,
}) => {
  const pathname = useInternalPathname();
  const [showModal, setShowModal] = useState(false);

  // Check country-specific retouch flow config
  const countryConfig = locale ? getCountrySpecificConfig(locale) : null;
  const isRetouchDisabled =
    disabled || countryConfig?.retouchFlow?.ringBuyPage === false;

  const {
    visitCount,
    shouldShowModal,
    isModalVisible,
    showModal: triggerShowModal,
    // hideModal,
    markModalShown,
    resetModalState,
    claimDiscount,
    isPreExistingUser,
  } = useRetouchFlow();

  // Get campaign data from CartContext
  const { getCampaignForProduct, campaignsLoading } = useCartStore(
    useShallow((s) => ({
      getCampaignForProduct: s.getCampaignForProduct,
      campaignsLoading: s.campaignsLoading,
    })),
  );

  // Get shared Modal context
  const ModalClient = useModal();

  // Check if Ring campaign is active (only if not ignoring campaigns)
  const ringCampaign = !ignoreCampaigns
    ? getCampaignForProduct('ring')
    : undefined;
  const hasCampaignActive = Boolean(ringCampaign);

  // Track if we've already logged the suppression event (prevent duplicates)
  const hasLoggedSuppression = useRef(false);

  // Show modal after delay when conditions are met
  useEffect(() => {
    if (isRetouchDisabled) return;
    if (!shouldShowModal) return;

    const isBuyPage = pathname.includes(BUY_PAGE_PATTERN);
    if (!isBuyPage) return;

    // Wait for campaigns to load before deciding (unless ignoring campaigns)
    if (!ignoreCampaigns && campaignsLoading) return;

    // Don't show if campaign is active
    if (hasCampaignActive) {
      // Track suppression event (once per session)
      if (!hasLoggedSuppression.current) {
        hasLoggedSuppression.current = true;

        const getDeviceType = (): string => {
          if (typeof window === 'undefined') return 'unknown';
          const width = window.innerWidth;
          if (width < 768) return 'mobile';
          if (width < 1024) return 'tablet';
          return 'desktop';
        };

        const analyticsData = {
          visitCount,
          threshold: VISITS_THRESHOLD,
          page: pathname,
          device: getDeviceType(),
          campaignTitle: ringCampaign?.title,
          campaignDiscountCode: ringCampaign?.discount_code,
          isPreExistingUser,
        };

        analytics.track(ANALYTICS_EVENTS.SUPPRESSED_BY_CAMPAIGN, analyticsData);

        // GTM dataLayer
        if (typeof window !== 'undefined' && allowsDataLayer()) {
          (
            window as Window & { dataLayer?: Record<string, unknown>[] }
          ).dataLayer?.push({
            event: GTM_EVENTS.SUPPRESSED_BY_CAMPAIGN,
            ...analyticsData,
          });
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      triggerShowModal();
      setShowModal(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [
    shouldShowModal,
    pathname,
    delayMs,
    isRetouchDisabled,
    triggerShowModal,
    hasCampaignActive,
    campaignsLoading,
    ignoreCampaigns,
    visitCount,
    ringCampaign,
    isPreExistingUser,
  ]);

  // Handle modal close (X button or backdrop)
  const handleClose = () => {
    markModalShown();
    setShowModal(false);
    ModalClient.hide();
  };

  // Handle claim discount
  const handleClaimDiscount = () => {
    claimDiscount();
    setShowModal(false);
    ModalClient.hide();
  };

  // Show modal using shared ModalContext when conditions are met
  useEffect(() => {
    if (showModal && isModalVisible) {
      ModalClient.set(
        <RetouchModalContent
          onClaimDiscount={handleClaimDiscount}
          onClose={handleClose}
        />,
      );
      ModalClient.setPortal(true); // Render in portal with high z-index
      ModalClient.setCloseBtnHidden(true); // Hide default close button, we have our own
      ModalClient.show();
    }
  }, [showModal, isModalVisible]);

  // Expose showRetouchModal() on window for testing purposes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Define the test function
    const testShowModal = () => {
      ModalClient.set(
        <RetouchModalContent
          onClaimDiscount={() => {
            ModalClient.hide();

            console.log(
              '[RetouchFlow] Test: Discount claimed - would redirect with ?affiliateCode=retouch',
            );
          }}
          onClose={() => {
            ModalClient.hide();

            console.log('[RetouchFlow] Test: Modal closed');
          }}
        />,
      );
      ModalClient.setPortal(true);
      ModalClient.setCloseBtnHidden(true);
      ModalClient.show();

      console.log(
        '[RetouchFlow] Test: Modal shown via window.showRetouchModal()',
      );
    };

    // Attach to window
    (window as Window & { showRetouchModal?: () => void }).showRetouchModal =
      testShowModal;

    // Cleanup on unmount
    return () => {
      delete (window as Window & { showRetouchModal?: () => void })
        .showRetouchModal;
    };
  }, [ModalClient]);

  // Context value
  // Map resetModalState to resetCounter for backwards compatibility with Add to Cart handlers
  const contextValue = {
    resetCounter: resetModalState,
    visitCount,
  };

  return (
    <RetouchFlowContext.Provider value={contextValue}>
      {children}
      {/* Modal is rendered via shared ModalContext */}
    </RetouchFlowContext.Provider>
  );
};

// Re-export everything for convenience
export { useRetouchFlow } from './useRetouchFlow';
export {
  useRetouchFlowContext,
  RetouchFlowContext,
} from './RetouchFlowContext';
export { RetouchModalContent } from './RetouchModalContent';
export * from './constants';

export default RetouchFlowProvider;
